import { ChangesetConflictDTO, ChangesetMergeDTO, ResolutionChoices } from "../models/ChangeRequestDTO";
import { ConflictPiece, Resolution, Resolutions, conflictKey, resolvedText, splitMerged } from "../utils/conflicts";
import { Fragment, useMemo, useState } from "react";

import { Marked } from "./RebaseConflicts";
import { SideLabels } from "../utils/labels";

/** Lines kept on each side of a conflict when the text between two of them folds. */
const Context = 3;
/** A run of unchanged lines shorter than this is shown whole. */
const FoldAbove = Context * 2 + 2;

interface IMergedContentProps {
    merge: ChangesetMergeDTO;
    /** The line conflicts of this op (`isLineConflict`). */
    conflicts: ChangesetConflictDTO[];
    resolutions: Resolutions;
    /** The author of an open change request resolves; everyone else reads. */
    canResolve: boolean;
    onResolve: (key: string, resolution: Resolution | undefined) => void;
    /** The words for each side, by who reads. */
    labels: SideLabels;
}

function Line(props: { no?: number; text: string; css?: string; base?: string }) {
    return (
        <tr className={`diff-row diff-same${props.css ? ` ${props.css}` : ""}`}>
            <td className="diff-no" aria-hidden>{props.no ?? ""}</td>
            <td className="diff-text">{props.base !== undefined ? <Marked base={props.base} value={props.text} /> : props.text || " "}</td>
        </tr>
    );
}

/** Past this many lines in one conflict, the words are not marked: each line is paired with the base line at the same place only. */
const MarkedLines = 200;

/** The base line a side's line is compared with for its word marks: the one at the same place, when the run is small enough to read that way. */
function baseFor(base: string, lines: string[]): (i: number) => string | undefined {
    const baseLines = base === "" ? [] : base.split("\n");
    if (lines.length > MarkedLines || baseLines.length > MarkedLines) return () => undefined;
    return (i) => baseLines[i];
}

/** A side longer than this shows its first lines and the rest on request: a run that long is read in the page, not here. */
const SideShown = 100;

/** The lines of one side of a conflict, each word that differs from the base line in its place marked. */
function Side(props: { label: string; lines: string[]; base: string; css: string }) {
    const [all, setAll] = useState(false);
    const base = baseFor(props.base, props.lines);
    const shown = all || props.lines.length <= SideShown * 1.5 ? props.lines : props.lines.slice(0, SideShown);
    const rest = props.lines.length - shown.length;
    return (
        <>
            <tr className={`merge-side ${props.css}`}>
                <td className="diff-no" aria-hidden />
                <td className="diff-text merge-side-label">{props.label}{props.lines.length > 1 ? ` · ${props.lines.length} lines` : ""}</td>
            </tr>
            {props.lines.length === 0 ? (
                <tr className={`diff-row merge-line ${props.css}`}>
                    <td className="diff-no" aria-hidden />
                    <td className="diff-text conflict-empty">(no lines)</td>
                </tr>
            ) : (
                shown.map((l, i) => <Line key={i} text={l} css={`merge-line ${props.css}`} base={base(i)} />)
            )}
            {rest > 0 ? (
                <tr className="diff-fold">
                    <td colSpan={2}>
                        <button type="button" onClick={() => setAll(true)}>⋯ Show the other {rest} lines of this side</button>
                    </td>
                </tr>
            ) : null}
        </>
    );
}

function ConflictBlock(props: { piece: ConflictPiece; resolution?: Resolution; canResolve: boolean; labels: SideLabels; onResolve: (r: Resolution | undefined) => void }) {
    const { piece, resolution, labels } = props;
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(piece.ours.join("\n"));
    const c = piece.conflict;
    const base = c.base ?? "";
    const where = c.line ? `line ${c.line}` : "here";

    return (
        <tbody className={`merge-conflict${resolution ? " resolved" : ""}`} aria-label={`Conflict at ${where}`}>
            <tr className="merge-conflict-head">
                <td className="diff-no" aria-hidden />
                <td className="diff-text">
                    <span className="merge-conflict-title">
                        {resolution ? `Resolved: ${labels.resolved[resolution.choice]}` : editing ? `Both changed ${where} · editing` : `Both changed ${where}`}
                    </span>
                    {props.canResolve ? (
                        <span className="merge-choices">
                            {resolution ? (
                                <button type="button" className="review-link" onClick={() => { props.onResolve(undefined); setEditing(false); }}>Change</button>
                            ) : editing ? null : (
                                <>
                                    <button type="button" className="merge-choice" onClick={() => props.onResolve({ choice: ResolutionChoices.Ours })}>{labels.keepOurs}</button>
                                    <button type="button" className="merge-choice" onClick={() => props.onResolve({ choice: ResolutionChoices.Theirs })}>{labels.takeTheirs}</button>
                                    <button type="button" className="merge-choice" onClick={() => { setDraft(piece.ours.join("\n")); setEditing(true); }}>Edit</button>
                                </>
                            )}
                        </span>
                    ) : labels.decides && !resolution ? (
                        <span className="merge-decides">{labels.decides}</span>
                    ) : null}
                </td>
            </tr>
            {resolution ? (
                (() => {
                    const chosen = resolvedText(c, resolution);
                    const chosenLines = chosen === "" ? [] : chosen.split("\n");
                    const baseAt = baseFor(base, chosenLines);
                    return chosenLines.length === 0
                        ? <Line text="(no lines)" css="merge-line merge-resolved conflict-empty" />
                        : chosenLines.map((l, i) => <Line key={i} no={piece.from + i} text={l} css="merge-line merge-resolved" base={baseAt(i)} />);
                })()
            ) : editing ? (
                <>
                    <tr className="merge-edit">
                        <td className="diff-no" aria-hidden />
                        <td className="diff-text">
                            <textarea aria-label={`Your lines at ${where}`} value={draft} rows={Math.max(3, draft.split("\n").length + 1)} onChange={(ev) => setDraft(ev.target.value)} />
                            <div className="merge-choices">
                                <button type="button" className="merge-choice" onClick={() => { props.onResolve({ choice: ResolutionChoices.Manual, value: draft }); setEditing(false); }}>Use these lines</button>
                                <button type="button" className="review-link" onClick={() => setEditing(false)}>Cancel</button>
                            </div>
                        </td>
                    </tr>
                    <tr className="merge-side">
                        <td className="diff-no" aria-hidden />
                        <td className="diff-text merge-side-label">For reference · {labels.ours.toLowerCase()} / {labels.theirs.toLowerCase()}</td>
                    </tr>
                    {piece.ours.map((l, i) => <Line key={`o${i}`} text={l} css="merge-line merge-ours" base={baseFor(base, piece.ours)(i)} />)}
                    {piece.theirs.map((l, i) => <Line key={`t${i}`} text={l} css="merge-line merge-theirs" base={baseFor(base, piece.theirs)(i)} />)}
                </>
            ) : (
                <>
                    <Side label={labels.ours} lines={piece.ours} base={base} css="merge-ours" />
                    <Side label={labels.theirs} lines={piece.theirs} base={base} css="merge-theirs" />
                </>
            )}
        </tbody>
    );
}

/**
 * The content as an update would leave it: the page's edits since the proposal and the author's merged, line by line, with each
 * place both changed shown as both versions. The author picks a side (or writes the lines) for each; the choices go with Update.
 */
export function MergedContent(props: IMergedContentProps) {
    const pieces = useMemo(() => splitMerged(props.merge, props.conflicts), [props.merge, props.conflicts]);
    const [expanded, setExpanded] = useState<Set<number>>(new Set());

    if (props.merge.content == null) return <div className="content-diff-empty">The content does not change.</div>;

    return (
        <div className="content-diff merged-content" aria-label="Merged content">
            <table className="content-diff-table">
                {pieces.map((piece, i) => {
                    if (piece.kind === "conflict") {
                        const key = conflictKey(piece.conflict);
                        return (
                            <ConflictBlock key={key} piece={piece} resolution={props.resolutions[key]} canResolve={props.canResolve} labels={props.labels}
                                onResolve={(r) => props.onResolve(key, r)} />
                        );
                    }
                    const folds = piece.lines.length > FoldAbove && !expanded.has(i);
                    if (!folds) {
                        return <tbody key={i}>{piece.lines.map((l, k) => <Line key={k} no={piece.from + k} text={l} />)}</tbody>;
                    }
                    // the first piece needs no lines before the fold, the last none after
                    const head = i === 0 ? 0 : Context;
                    const tail = i === pieces.length - 1 ? 0 : Context;
                    const hidden = piece.lines.length - head - tail;
                    return (
                        <Fragment key={i}>
                            <tbody>{piece.lines.slice(0, head).map((l, k) => <Line key={k} no={piece.from + k} text={l} />)}</tbody>
                            <tbody>
                                <tr className="diff-fold">
                                    <td colSpan={2}>
                                        <button type="button" onClick={() => setExpanded((prev) => new Set(prev).add(i))}>
                                            ⋯ Show {hidden} unchanged line{hidden === 1 ? "" : "s"}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                            <tbody>
                                {piece.lines.slice(piece.lines.length - tail).map((l, k) => (
                                    <Line key={k} no={piece.from + piece.lines.length - tail + k} text={l} />
                                ))}
                            </tbody>
                        </Fragment>
                    );
                })}
            </table>
        </div>
    );
}
